import { useEffect, useRef } from 'react'
import { Spinner } from '../components/Spinner'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../auth/useAuth'
import { getTenantFromHostname } from '../lib/tenant'

// getTenantFromHostname/URLSearchParams.get both use '' to mean "absent", which
// `??` would not catch — so falsy-but-non-empty values are filtered explicitly
// instead of relying on `||`.
function firstNonEmpty(...values: (string | null)[]): string | null {
  return values.find((value) => !!value) ?? null
}

export function OAuthCallback() {
  const { setAuth } = useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const exchanged = useRef(false)

  useEffect(() => {
    if (exchanged.current) return
    exchanged.current = true

    const params = new URLSearchParams(window.location.search)
    const code = params.get('handoff_code') ?? params.get('code')
    const cbeError = params.get('error')

    // tenant_id comes from CBE's callback param (it already knows the target
    // tenant before any exchange happens) and from the hostname as a fallback
    // (e.g. local dev redirect that landed straight on the tenant subdomain).
    const hostTenantId = firstNonEmpty(getTenantFromHostname())
    const tenantId = firstNonEmpty(params.get('tenant_id'), hostTenantId)

    if (!code) {
      // A CBE-reported error (e.g. an apparently-active session whose token
      // exchange never gets a code) is a failed authentication, not a plain
      // sign-out — routes to login with the auth-failed state (MS-682 Scenario B).
      setAuth(false, undefined, cbeError ? 'authFailed' : undefined)
      navigate('/', { replace: true })
      return
    }

    if (!tenantId) {
      setAuth(false)
      navigate('/', { replace: true })
      return
    }

    // AC2: this callback landed on a host that isn't the target tenant's own
    // subdomain (e.g. the common host CBE always redirects to) — forward the
    // code and tenant_id there instead of exchanging here. The tenant host
    // runs its own /exchange and sets its own host-only cookie; no token
    // crosses a host boundary.
    if (hostTenantId !== tenantId) {
      fetch(`/api/v1/auth/config?tenant_id=${encodeURIComponent(tenantId)}`)
        .then(async (resp) => {
          const data = (await resp.json().catch(() => ({}))) as { base_studio_url?: string | null }
          if (!resp.ok || !data.base_studio_url) {
            setAuth(false, undefined, 'authFailed')
            navigate('/', { replace: true })
            return
          }
          const port = window.location.port ? `:${window.location.port}` : ''
          const target =
            `${window.location.protocol}//${tenantId}.${data.base_studio_url}${port}` +
            `/oauth2/callback?code=${encodeURIComponent(code)}&tenant_id=${encodeURIComponent(tenantId)}`
          window.location.replace(target)
        })
        .catch(() => {
          setAuth(false, undefined, 'authFailed')
          navigate('/', { replace: true })
        })
      return
    }

    // AC3: already on the correct tenant host — exchange directly, no redirect.
    fetch('/api/v1/auth/exchange', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, tenant_id: tenantId }),
    })
      .then(async (resp) => {
        if (resp.status === 403) {
          setAuth(false)
          navigate('/access-denied', { replace: true })
          return
        }
        if (!resp.ok) {
          setAuth(false, undefined, 'authFailed')
          navigate('/', { replace: true })
          return
        }
        const body = (await resp.json().catch(() => ({}))) as {
          displayName?: string | null
          email?: string | null
          tenantUrl?: string | null
          tenantId?: string | null
          isGlobalAdmin?: boolean
          baseStudioUrl?: string | null
          agentsContracted?: boolean
          studioRole?: 'Admin' | 'Editor' | null
          entitlementsAvailable?: boolean
        }
        setAuth(true, {
          displayName: body.displayName,
          email: body.email,
          tenantUrl: body.tenantUrl,
          tenantId: body.tenantId,
          isGlobalAdmin: body.isGlobalAdmin ?? false,
          baseStudioUrl: body.baseStudioUrl ?? null,
          // Must be forwarded explicitly: setAuth defaults a missing value to `true`,
          // so dropping it here left the first post-login render showing the Agents
          // surfaces as contracted until a reload re-read /auth/session.
          agentsContracted: body.agentsContracted ?? true,
          studioRole: body.studioRole ?? null,
          // Same reason as agentsContracted above (MS-866): dropping it here left the
          // first post-login render fully healthy, and the degraded-entitlements state
          // only appeared once a focus revalidation re-read /auth/session.
          entitlementsAvailable: body.entitlementsAvailable ?? true,
        })
        const savedPath = sessionStorage.getItem('redirect_after_login')
        sessionStorage.removeItem('redirect_after_login')
        navigate(savedPath ?? '/', { replace: true })
      })
      .catch(() => {
        setAuth(false, undefined, 'authFailed')
        navigate('/', { replace: true })
      })
  }, [navigate, setAuth])

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-app)', color: 'var(--text-tertiary)', gap: 10 }}>
      <Spinner size={18} />
      {t('oauthCallback.signingIn')}
    </div>
  )
}
